import { ReactElement, useState, createContext, useContext } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { LAYOUT_CONFIG, LayoutMode } from "../../constants/layout";
import Header from "./components/Header/Header";
import MobileBottomNav from "./components/Menu/MobileBottomNav";

interface LayoutContextType {
  layoutMode: LayoutMode;
  setLayoutMode: (mode: LayoutMode) => void;
  isMenuOpen: boolean;
  toggleMenu: () => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) throw new Error('useLayout must be used within LayoutProvider');
  return context;
};

interface LayoutProps {
  children: ReactElement;
  mode?: LayoutMode;
}

export default function Layout({ children, mode = 'vertical' }: LayoutProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const layoutMode: LayoutMode = isMobile ? 'vertical' : 'horizontal';
  const [isMenuOpen, setIsMenuOpen] = useState(true);

  const handleToggle = () => {
    setIsMenuOpen(prev => !prev);
    document.body.classList.toggle("sidebar-enable");
    if (!isMobile) document.body.classList.toggle("vertical-collpsed");
  };

  const handleLayoutModeChange = (newMode: LayoutMode) => {};

  const contextValue: LayoutContextType = {
    layoutMode,
    setLayoutMode: handleLayoutModeChange,
    isMenuOpen,
    toggleMenu: handleToggle,
  };

  const topMargin = isMobile ? 70 : LAYOUT_CONFIG.headerHeight;

  const bottomMargin = isMobile ? 100 : 0;

  const mainStyles = {
    flexGrow: 1,
    p: 0,
    backgroundColor: "#f3f3f9",
    marginTop: `${topMargin}px`,
    width: '100%',
    height: `calc(100vh - ${topMargin}px)`,
    overflowY: 'auto',
    paddingBottom: `${bottomMargin}px`,
  };

  return (
    <LayoutContext.Provider value={contextValue}>
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <Header
          open={isMenuOpen}
          onToggle={handleToggle}
          layoutMode={layoutMode}
          onLayoutModeChange={handleLayoutModeChange}
        />
        <Box component="main" sx={mainStyles}>
          {children}
        </Box>
        {isMobile && <MobileBottomNav />}
      </Box>
    </LayoutContext.Provider>
  );
}
